import { useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import {
  getPatient,
  uploadIdentificationPhoto,
  verifyPatientIdentity,
  flagPatientIdentity,
} from '../api/patients.js'
import { pngDateTime } from '../utils/format.js'

import PatientIdentityCard from './PatientIdentityCard.jsx'
import IdentityConsentModal from './IdentityConsentModal.jsx'
import IdentityFlagReasonModal from './IdentityFlagReasonModal.jsx'
import CameraCaptureModal from './CameraCaptureModal.jsx'

/**
 * PatientIdentityBanner — full-width identity banner at the top of the
 * encounter editor.
 *
 * Purpose: give clinicians a one-glance answer to "am I looking at the
 * chart of the person in front of me?" — the highest-value patient-safety
 * question in the whole platform.
 *
 * States rendered:
 *   High Confidence     -> quiet green banner, "Verified by X on Y", small flag link
 *   Moderate Confidence -> amber banner, "Photo captured but not verified", capture-and-verify CTA
 *   Unverified          -> amber banner, "No identification photo", capture CTA
 *   Needs Review        -> rose banner, "Identity requires re-verification", capture-and-verify CTA
 *
 * The three write actions this component drives:
 *   1. Upload a photo (opens ConsentModal on first capture; auto-transitions
 *      the record to self_submitted via the multipart PATCH)
 *   2. Verify identity (clinician has visually confirmed → clinician_verified)
 *   3. Flag identity (identity requires verification → opens the reason picker)
 *
 * Read-only visitors (patient viewing their own chart, org_admin, etc.)
 * see just the confidence badge — no action buttons. `canWrite` gates
 * the write UI; the backend re-enforces on every action.
 */
export default function PatientIdentityBanner({
  patientId,
  canWrite = true,
  className = '',
}) {
  const qc = useQueryClient()
  const fileInputRef = useRef(null)
  const [showConsent, setShowConsent] = useState(false)
  const [pendingFile, setPendingFile] = useState(null)
  const [showFlagModal, setShowFlagModal] = useState(false)
  const [showCamera, setShowCamera] = useState(false)
  const [error, setError] = useState('')

  // Fetch the full patient record so we get identification_photo_url,
  // identity_confidence_display, identity_verified_by_name, etc. from
  // the serializer. Cached by React Query — sibling encounter reads
  // share the same cache entry.
  const patientQ = useQuery({
    queryKey: ['patient', String(patientId)],
    queryFn: () => getPatient(patientId),
    enabled: Boolean(patientId),
    staleTime: 30_000,
  })
  const patient = patientQ.data

  const invalidatePatient = () =>
    qc.invalidateQueries({ queryKey: ['patient', String(patientId)] })

  // ---- Upload ----------------------------------------------------------
  const uploadMut = useMutation({
    mutationFn: (file) => uploadIdentificationPhoto(patientId, file),
    onSuccess: () => {
      setPendingFile(null)
      setShowConsent(false)
      setError('')
      invalidatePatient()
    },
    onError: (err) => {
      const msg = err?.response?.data?.detail || err?.message || 'Upload failed.'
      setError(typeof msg === 'string' ? msg : JSON.stringify(msg))
    },
  })

  // ---- Verify ----------------------------------------------------------
  const verifyMut = useMutation({
    mutationFn: () => verifyPatientIdentity(patientId, { method: 'clinician_verified' }),
    onSuccess: () => {
      setError('')
      invalidatePatient()
    },
    onError: (err) => {
      const msg = err?.response?.data?.detail || err?.message || 'Verification failed.'
      setError(typeof msg === 'string' ? msg : JSON.stringify(msg))
    },
  })

  // ---- Flag ------------------------------------------------------------
  const flagMut = useMutation({
    mutationFn: (reason) => flagPatientIdentity(patientId, reason),
    onSuccess: () => {
      setShowFlagModal(false)
      setError('')
      invalidatePatient()
    },
    onError: (err) => {
      const msg = err?.response?.data?.detail || err?.message || 'Flag failed.'
      setError(typeof msg === 'string' ? msg : JSON.stringify(msg))
    },
  })

  function onFilePicked(e) {
    const file = e.target.files?.[0]
    if (!file) return
    // Both upload paths (file picker and webcam) funnel through the same
    // consent modal — consent is about the PATIENT, not about how the
    // photo happened to be captured.
    setPendingFile(file)
    setShowConsent(true)
    // reset the input so re-picking the same file re-triggers onChange
    e.target.value = ''
  }

  function onCameraCaptured(file) {
    setShowCamera(false)
    setPendingFile(file)
    setShowConsent(true)
  }

  function onConsentAgree() {
    if (pendingFile) uploadMut.mutate(pendingFile)
  }

  function onConsentDecline() {
    setPendingFile(null)
    setShowConsent(false)
  }

  // ---- Loading / no-data states ---------------------------------------
  if (!patientId) return null
  if (patientQ.isLoading) {
    return (
      <div
        className={
          'border rounded-md px-3 py-2 text-xs text-slate-500 bg-slate-50 ' +
          'border-slate-200 ' + className
        }
      >
        Loading identity …
      </div>
    )
  }
  if (!patient) return null

  // ---- Tier resolution ------------------------------------------------
  const conf = patient.identity_confidence_display  // 'High Confidence' | 'Moderate Confidence' | 'Unverified'
  const status = patient.identity_verification_status
  const isVerified = conf === 'High Confidence'
  const isModerate = conf === 'Moderate Confidence'
  const isNeedsReview = status === 'needs_review'

  const tierClasses = isVerified
    ? 'bg-emerald-50 border-emerald-200'
    : isNeedsReview
    ? 'bg-rose-50 border-rose-200'
    : 'bg-amber-50 border-amber-200'

  const headline = isVerified
    ? 'Identity verified'
    : isNeedsReview
    ? 'Identity requires re-verification'
    : isModerate
    ? 'Identity photo on file — not yet clinician-verified'
    : 'No identification photo on file'

  const subhead = isVerified
    ? 'Confidence is high. This record has been confirmed by a clinician.'
    : isNeedsReview
    ? (patient.identity_flag_reason
      ? `A clinician flagged this record: ${labelForReason(patient.identity_flag_reason)}. Re-verify at this visit.`
      : 'A clinician flagged this record. Re-verify at this visit.')
    : isModerate
    ? 'The patient submitted this photo at registration. Please visually confirm at this visit.'
    : 'Capture a Clinical Identification Photo to help every clinician confirm the right patient.'

  return (
    <div className={'mb-3 ' + className}>
      <div
        className={
          'border rounded-md px-3 py-2 flex flex-wrap items-center gap-3 ' + tierClasses
        }
      >
        <div className="flex-1 min-w-0 flex items-center gap-3">
          <PatientIdentityCard
            patient={patient}
            size="md"
            showBadge={false}
            showVerifiedBy={false}
          />
          <div className="min-w-0">
            <div className="text-sm font-semibold text-slate-900 truncate">
              {headline}
            </div>
            <div className="text-xs text-slate-600 leading-snug mt-0.5">
              {subhead}
            </div>
            {isVerified && patient.identity_verified_by_name && (
              <div className="text-[11px] text-slate-500 mt-0.5">
                Verified by <span className="font-medium">{patient.identity_verified_by_name}</span>
                {patient.identity_verified_at && (
                  // Route the verified-at timestamp through the same PNG
                  // timezone formatter used elsewhere in the clinical UI so
                  // 8pm UTC doesn't render as "Jul 09" here and "Jul 08" on
                  // the encounter card two lines below. See utils/format.js.
                  <> · {pngDateTime(patient.identity_verified_at)}</>
                )}
              </div>
            )}
          </div>
        </div>

        {canWrite && (
          // Branch on identity_verification_status FIRST, on photo-url
          // presence SECOND. The status is the source of truth; the URL is
          // just how the FE happens to have a picture to show. Branching
          // on URL first put a "verified but no photo file" record (a real
          // data anomaly we've seen after admin-side Clear) into the
          // Take/Upload branch, which contradicted the emerald headline.
          <div className="flex items-center gap-2 flex-shrink-0">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={onFilePicked}
            />
            {isVerified ? (
              // High Confidence — the primary read is the emerald headline
              // + "Verified by X". The action ("Flag for re-verification")
              // is deliberately a subtle text link, not a prominent
              // button, so it doesn't visually contradict the state.
              <button
                type="button"
                onClick={() => setShowFlagModal(true)}
                className="text-[11px] text-amber-800 hover:text-amber-900 underline decoration-dotted underline-offset-2"
                title="Photo looks wrong or outdated? Flag to move this record to Needs review with a structured reason."
              >
                Flag for re-verification
              </button>
            ) : !patient.identification_photo_url ? (
              // No photo on file — offer both capture paths.
              <>
                <button
                  type="button"
                  onClick={() => setShowCamera(true)}
                  disabled={uploadMut.isPending}
                  className="text-xs px-3 py-1.5 rounded bg-wantok-700 hover:bg-wantok-800 text-white font-medium disabled:opacity-60"
                  title="Open the camera and take a live photo"
                >
                  Take photo
                </button>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadMut.isPending}
                  className="text-xs px-3 py-1.5 rounded border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 font-medium disabled:opacity-60"
                  title="Choose an existing image file"
                >
                  Upload photo
                </button>
              </>
            ) : (
              // Moderate / Needs Review — photo present, needs a clinician
              // to visually confirm (or reject via retake). Verify is the
              // primary CTA; Retake/Upload are secondary.
              <>
                <button
                  type="button"
                  onClick={() => setShowCamera(true)}
                  disabled={uploadMut.isPending}
                  className="text-xs px-2.5 py-1.5 rounded border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 disabled:opacity-60"
                  title="Retake with the camera"
                >
                  Retake
                </button>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadMut.isPending}
                  className="text-xs px-2.5 py-1.5 rounded border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 disabled:opacity-60"
                  title="Replace the current photo with an uploaded file"
                >
                  Upload
                </button>
                <button
                  type="button"
                  onClick={() => verifyMut.mutate()}
                  disabled={verifyMut.isPending}
                  className="text-xs px-3 py-1.5 rounded bg-emerald-700 hover:bg-emerald-800 text-white font-medium disabled:opacity-60"
                >
                  {verifyMut.isPending ? 'Verifying…' : 'Verify identity'}
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {error && (
        <div className="text-[11px] text-rose-700 mt-1">
          {error}
        </div>
      )}

      {showConsent && (
        <IdentityConsentModal
          isSubmitting={uploadMut.isPending}
          onAgree={onConsentAgree}
          onDecline={onConsentDecline}
        />
      )}

      {showFlagModal && (
        <IdentityFlagReasonModal
          patientName={patient.full_name}
          isSubmitting={flagMut.isPending}
          onSubmit={(reason) => flagMut.mutate(reason)}
          onCancel={() => setShowFlagModal(false)}
        />
      )}

      {showCamera && (
        <CameraCaptureModal
          onCapture={onCameraCaptured}
          onCancel={() => setShowCamera(false)}
        />
      )}
    </div>
  )
}

// Mirror the enum labels used server-side (see PatientProfile
// IDENTITY_FLAG_REASON_CHOICES). Kept local so this component has no
// upstream data dependency; the enum drift risk is low because the set
// is very stable and the UI only renders the label, never posts it.
const REASON_LABELS = {
  photo_outdated:      'Photo outdated',
  photo_mismatch:      'Photo mismatch',
  duplicate_suspected: 'Duplicate suspected',
  name_discrepancy:    'Name discrepancy',
  other:               'Other',
}
function labelForReason(code) {
  return REASON_LABELS[code] || 'Identity concern'
}
